import { Component, Input } from '@angular/core';
import {
    FlyFreelyError,
    FlyFreelyLoggingService,
    LotamFull,
    NoticeAcknowledgement,
    NoticesService,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import { TableColumn, TableConfig } from '@flyfreely-portal-ui/flyfreely-table';
import { FormatDateTimePipe, FormatPersonPipe } from '@flyfreely-portal-ui/ui';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Subject, takeUntil } from 'rxjs';

interface AcknowledgementRow {
    /** The acknowledger's full name, used for display, searching and sorting. */
    personName: string;
    /** Raw ISO acknowledgement time, used for chronological sorting. */
    acknowledgementTime: string;
    /** Formatted acknowledgement time, used for display and searching. */
    acknowledgementDate: string;
}

@Component({
    selector: 'notice-acknowledgements-dialogue',
    templateUrl: './notice-acknowledgements-dialogue.component.html'
})
export class NoticeAcknowledgementsDialogueComponent {
    @Input() notice: LotamFull;
    @Input() managingOrganisationId: number;
    @Input() timeZone?: string;

    rows: AcknowledgementRow[] = [];
    availableColumns: TableColumn<AcknowledgementRow>[];
    tableConfig: TableConfig<AcknowledgementRow>;

    working = false;
    private workTracker = new WorkTracker();
    private ngUnsubscribe$ = new Subject<void>();

    constructor(
        private noticesService: NoticesService,
        private modal: BsModalRef<NoticeAcknowledgementsDialogueComponent>,
        private logging: FlyFreelyLoggingService,
        private formatDateTime: FormatDateTimePipe,
        private formatPerson: FormatPersonPipe
    ) {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.working = working));
    }

    ngOnInit() {
        this.availableColumns = [
            {
                name: 'Name',
                value: 'personName',
                searchable: 'text',
                selectable: false,
                defaultSelection: true,
                sortable: true
            },
            {
                name: 'Acknowledgement Date',
                key: 'acknowledgementTime',
                value: 'acknowledgementDate',
                searchable: 'text',
                selectable: false,
                defaultSelection: true,
                sortable: true,
                compareFunction: (a, b) =>
                    (a.rawData ?? '').localeCompare(b.rawData ?? '')
            }
        ];

        this.tableConfig = {
            limit: null,
            actions: [],
            timeZone: this.timeZone
        };

        this.refresh();
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    private refresh() {
        if (this.notice?.id == null || this.managingOrganisationId == null) {
            return;
        }
        this.noticesService
            .findNoticeAcknowledgements(
                this.notice.id,
                this.managingOrganisationId
            )
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: acknowledgements =>
                    (this.rows = acknowledgements.map(a => this.toRow(a))),
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error while loading acknowledgements: ${error.message}`
                    )
            })
            .add(this.workTracker.createTracker());
    }

    private toRow(acknowledgement: NoticeAcknowledgement): AcknowledgementRow {
        return {
            personName: this.formatPerson.transform(acknowledgement.person),
            acknowledgementTime: acknowledgement.acknowledgementTime,
            acknowledgementDate:
                this.formatDateTime.transform(
                    acknowledgement.acknowledgementTime,
                    this.timeZone
                ) ?? ''
        };
    }

    onClose() {
        this.modal.hide();
    }
}
